import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Fragment, useCallback, useState, useRef, RefObject, createElement } from 'react';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { TableContainer } from '@mui/material';
import { DataTableRow, DataTableDraggableRowProps, DataTableVariant } from './TableTypes';
import { StyledTable, StyledTableBody } from './StyledTableComponents';
import { DraggableTableRow } from './DraggableTableRow';

interface DraggableTableBodyRowsProps<TRow extends DataTableRow>
  extends Pick<
    DataTableDraggableRowProps<TRow>,
    'rows' | 'columns' | 'onRowClick' | 'onRowOrderChange' | 'renderRow'
  > {
  variant: DataTableVariant;
  overColumnId?: string | null;
  draggedColumnId?: string | null;
  isColumnReorderAllowed?: boolean;
  tableContainerRef?: RefObject<HTMLDivElement>;
  isColumnResizeAllowed?: boolean;
}

export function DraggableTableBodyRows<TRow extends DataTableRow = any>({
  rows,
  onRowOrderChange,
  renderRow,
  onRowClick,
  variant,
  overColumnId,
  columns,
  draggedColumnId,
  isColumnReorderAllowed,
  tableContainerRef,
  isColumnResizeAllowed,
  ...dataTableProps
}: DraggableTableBodyRowsProps<TRow>) {
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const [overlayTableWidth, setOverlayTableWidth] = useState<number | undefined>(undefined);
  const rowRefs = useRef<{ [key: string]: HTMLTableRowElement | null }>({});
  const draggedRow = rows.find((row) => row.id === draggedItemId);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      setDraggedItemId(event.active.id + '');
      // Measure visible table width
      if (tableContainerRef && tableContainerRef.current) {
        setOverlayTableWidth(tableContainerRef.current.clientWidth);
      } else {
        setOverlayTableWidth(undefined);
      }
    },
    [tableContainerRef],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (active.id !== over?.id) {
        const sourceIndex = rows.findIndex((row) => row.id === active.id);
        const destinationIndex = rows.findIndex((row) => row.id === over?.id);
        onRowOrderChange?.({ sourceIndex, destinationIndex });
      }
      setDraggedItemId(null);
    },
    [rows, onRowOrderChange],
  );

  const renderSingleRow = (row: TRow, rowIdx: number, rowKey: string) => {
    const renderedRow =
      renderRow &&
      renderRow({
        rowIdx,
        row,
        rowKey,
      });

    if (renderedRow) {
      return renderedRow;
    }

    // Only attach ref for the base (non-overlay) row using React.createElement
    // used React.createElement to avoid the need to forward ref to DraggableTableRow and dealing with TS issues
    return createElement(DraggableTableRow, {
      onClick: !!onRowClick
        ? (e, column) => {
            //verify that the click is on
            //the row (and not on a row's "actions menu popover" backdrop)
            if (e.target instanceof Element && e.target?.matches('tr *')) {
              onRowClick(e, row, column);
            }
          }
        : undefined,
      key: row.id,
      row: row,
      isOverlay: false,
      isBeingDragged: draggedItemId === row.id,
      overColumnId: overColumnId,
      draggedColumnId: draggedColumnId,
      variant: variant,
      columns: columns,
      isColumnReorderAllowed: isColumnReorderAllowed,
      isColumnResizeAllowed: isColumnResizeAllowed,
      ...dataTableProps,
      ref: (node: HTMLTableRowElement | null) => {
        rowRefs.current[row.id] = node;
      },
    });
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
    >
      <SortableContext items={rows} strategy={verticalListSortingStrategy}>
        {rows.map((row, rowIdx) => (
          <Fragment key={`wrapper_${row.id}`}>
            {renderSingleRow(row, rowIdx, `base_row_${rowIdx}`)}
            {row?.nestedRows &&
              row.nestedRows.map((row: any, idx: number) =>
                renderSingleRow(row, idx, `nested_row_${row.id}`),
              )}
          </Fragment>
        ))}
      </SortableContext>
      <DragOverlay
        style={{
          transformOrigin: '0 0',
        }}
      >
        {draggedRow && (
          <TableContainer
            sx={{
              overflow: 'hidden',
              height: '100%',
              width: overlayTableWidth ? `${overlayTableWidth}px` : 'auto',
              maxWidth: overlayTableWidth ? `${overlayTableWidth}px` : 'none',
            }}
          >
            <StyledTable>
              <StyledTableBody>
                <DraggableTableRow
                  key={draggedRow.id}
                  row={draggedRow}
                  isOverlay={true}
                  isBeingDragged={true}
                  overColumnId={overColumnId}
                  draggedColumnId={draggedColumnId}
                  variant={variant}
                  columns={columns}
                  isColumnReorderAllowed={isColumnReorderAllowed}
                  isColumnResizeAllowed={isColumnResizeAllowed}
                  {...dataTableProps}
                />
              </StyledTableBody>
            </StyledTable>
          </TableContainer>
        )}
      </DragOverlay>
    </DndContext>
  );
}

export default DraggableTableBodyRows;
